import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, ChevronRight, ExternalLink, Copy, Check, Moon, Sun, X, Key, LogIn, LogOut, Shield, Zap, AlertTriangle, TrendingUp, Plane, MapPin, Lock, BookOpen } from 'lucide-react';

const API_BASE = 'https://api.manuk.ai/v1';
// Hashed access key - SHA-256 hash of the actual password
const ACCESS_KEY_HASH = '69ce591d7fc8c1c3814c7af33e46ad3f4c971739038c619777f1b6c73edf0a23';

const ManukapiDocs = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authKey, setAuthKey] = useState('');
  const [authError, setAuthError] = useState('');
  const [activeSection, setActiveSection] = useState('introduction');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedCode, setCopiedCode] = useState('');
  const [isDark, setIsDark] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    // Check localStorage for existing auth
    const storedAuth = localStorage.getItem('manukai_auth');
    if (storedAuth === ACCESS_KEY_HASH) {
      setIsAuthenticated(true);
    }
    
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(darkModeQuery.matches);
    
    const darkModeListener = (e) => setIsDark(e.matches);
    darkModeQuery.addEventListener('change', darkModeListener);
    
    return () => darkModeQuery.removeEventListener('change', darkModeListener);
  }, []);

  const hashString = async (str) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  const handleAuthentication = async () => {
    setAuthError('');
    const hashedInput = await hashString(authKey);
    
    if (hashedInput === ACCESS_KEY_HASH) {
      setIsAuthenticated(true);
      localStorage.setItem('manukai_auth', ACCESS_KEY_HASH);
      setAuthKey('');
    } else {
      setAuthError('Invalid access key. Please contact marc@manuk.ai for access.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('manukai_auth');
    setActiveSection('introduction');
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      
      let currentSection = 'introduction';
      Object.keys(sectionRefs.current).forEach((sectionId) => {
        const element = sectionRefs.current[sectionId];
        if (element && element.offsetTop <= scrollPosition) {
          currentSection = sectionId;
        }
      });
      
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const navigation = [
    { id: 'introduction', title: 'Introduction', category: 'Getting Started', keywords: ['intro', 'overview', 'welcome'], icon: Shield },
    { id: 'authentication', title: 'Authentication', category: 'Getting Started', keywords: ['auth', 'api key', 'token'], icon: Key },
    { id: 'quickstart', title: 'Quick Start', category: 'Getting Started', keywords: ['quick', 'start', 'tutorial'], icon: Zap },
    { id: 'pagination', title: 'Pagination Best Practices', category: 'Getting Started', keywords: ['pagination', 'limit', 'offset', 'has_more'], icon: ChevronRight },
    
    { id: 'general-intelligence', title: 'Live Intelligence', category: 'General Security', keywords: ['intelligence', 'security', 'threats', 'general'], icon: AlertTriangle },
    { id: 'general-intelligence-detail', title: 'Detail View', category: 'General Security', keywords: ['detail', 'single', 'full'], icon: Search },
    { id: 'general-intelligence-advanced', title: 'Advanced Filtering', category: 'General Security', keywords: ['filter', 'search', 'advanced'], icon: Search },
    { id: 'general-taxonomy', title: 'Threat Taxonomy', category: 'General Security', keywords: ['taxonomy', 'categories', 'reference', 'general'], icon: BookOpen, badge: 'Soon' },
    { id: 'general-live-alerts', title: 'Live Alerts', category: 'General Security', keywords: ['alert', 'grouped', 'live'], icon: AlertTriangle, badge: 'Soon' },
    { id: 'general-predictive', title: 'Predictive Alerts', category: 'General Security', keywords: ['prediction', 'forecast', 'future'], icon: TrendingUp, badge: 'Soon' },
    
    { id: 'aviation-intelligence', title: 'Live Intelligence', category: 'Aviation Security', keywords: ['aviation', 'aircraft', 'flight', 'intelligence'], icon: Plane },
    { id: 'aviation-intelligence-detail', title: 'Detail View', category: 'Aviation Security', keywords: ['aviation', 'detail', 'single'], icon: Search },
    { id: 'aviation-intelligence-advanced', title: 'Advanced Filtering', category: 'Aviation Security', keywords: ['aviation', 'filter', 'advanced'], icon: Search },
    { id: 'aviation-taxonomy', title: 'Threat Taxonomy', category: 'Aviation Security', keywords: ['taxonomy', 'categories', 'reference', 'aviation'], icon: BookOpen },
    { id: 'aviation-live-alerts', title: 'Live Alerts', category: 'Aviation Security', keywords: ['aviation', 'alert', 'live'], icon: AlertTriangle, badge: 'Soon' },
    { id: 'aviation-predictive', title: 'Predictive Alerts', category: 'Aviation Security', keywords: ['aviation', 'prediction', 'forecast'], icon: TrendingUp, badge: 'Soon' },
    
    { id: 'sources', title: 'News Sources', category: 'Reference', keywords: ['source', 'news', 'publisher'], icon: ExternalLink },
    { id: 'health', title: 'Health & Status', category: 'Reference', keywords: ['health', 'monitor', 'status'], icon: Shield },
  ];

  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    
    const query = searchQuery.toLowerCase();
    return navigation.filter(item => 
      item.title.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query) ||
      item.keywords.some(keyword => keyword.includes(query))
    );
  }, [searchQuery]);

  useEffect(() => {
    setShowSearchResults(searchQuery.length >= 2);
  }, [searchQuery]);

  const handleSearchResultClick = (sectionId) => {
    setActiveSection(sectionId);
    setSearchQuery('');
    setShowSearchResults(false);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const groupedNav = navigation.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const Endpoint = ({ method, path, description, auth, params, response, example }) => (
    <div className={`mb-8 rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-2xl ${
      isDark 
        ? 'bg-gray-800/50 backdrop-blur-xl border border-gray-700/50' 
        : 'bg-white/80 backdrop-blur-xl border border-gray-200/50'
    }`}>
      <div className={`p-5 border-b ${
        isDark 
          ? 'bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-gray-700/50' 
          : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200'
      }`}>
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <span className={`px-3 py-1 rounded-lg text-xs font-bold shadow-md ${
            method === 'GET' ? (isDark ? 'bg-green-900/80 text-green-200' : 'bg-green-100 text-green-800') :
            method === 'POST' ? (isDark ? 'bg-blue-900/80 text-blue-200' : 'bg-blue-100 text-blue-800') :
            (isDark ? 'bg-purple-900/80 text-purple-200' : 'bg-purple-100 text-purple-800')
          }`}>
            {method}
          </span>
          <code className={`text-sm font-mono ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{path}</code>
        </div>
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>
        {auth && (
          <div className={`mt-3 text-xs flex items-center gap-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            <Shield className="w-3 h-3" />
            Requires authentication
          </div>
        )}
      </div>
      
      {params && (
        <div className={`p-5 border-b ${isDark ? 'bg-gray-800/30 border-gray-700/50' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-semibold text-sm mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Parameters</h4>
          <p className={`text-xs mb-3 italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            All parameters are optional unless stated otherwise.
          </p>
          <div className="space-y-2">
            {params.map((param, idx) => (
              <div key={idx} className="flex gap-3 text-sm flex-wrap items-start">
                <code className={`font-mono font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{param.name}</code>
                <span className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{param.type}</span>
                <span className={`flex-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{param.description}</span>
                {param.default && <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>default: {param.default}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {example && (
        <div className={`p-5 ${isDark ? 'bg-gray-900/30' : 'bg-gray-50'}`}>
          <div className="flex justify-between items-center mb-3">
            <h4 className={`font-semibold text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Example Request</h4>
            <button
              onClick={() => copyToClipboard(example, path)}
              className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                isDark 
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              {copiedCode === path ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copiedCode === path ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className={`p-4 rounded-lg text-xs overflow-x-auto shadow-inner ${
            isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-900 text-gray-100'
          }`}>
            <code>{example}</code>
          </pre>
        </div>
      )}

      {response && (
        <div className={`p-5 ${isDark ? 'bg-gray-800/30' : 'bg-white'}`}>
          <h4 className={`font-semibold text-sm mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Response</h4>
          <pre className={`p-4 rounded-lg text-xs overflow-x-auto border shadow-inner ${
            isDark ? 'bg-gray-900/50 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-800'
          }`}>
            <code>{response}</code>
          </pre>
        </div>
      )}
    </div>
  );

  const Section = ({ id, title, children }) => (
    <section 
      id={id} 
      ref={el => sectionRefs.current[id] = el}
      className="mb-16 scroll-mt-24"
    >
      <h2 className={`text-3xl font-bold mb-6 pb-3 border-b-2 ${
        isDark 
          ? 'text-white border-blue-500' 
          : 'text-gray-900 border-blue-500'
      }`}>
        {title}
      </h2>
      {children}
    </section>
  );

  const ComingSoonBadge = () => (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-bold ${
      isDark ? 'bg-yellow-900/60 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
    }`}>
      <Zap className="w-3 h-3" />
      Coming Soon
    </div>
  );

  // Authentication Screen
  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50'
      }`}>
        <div className={`w-full max-w-md p-8 rounded-2xl shadow-2xl ${
          isDark 
            ? 'bg-gray-800/80 backdrop-blur-xl border border-gray-700/50' 
            : 'bg-white/80 backdrop-blur-xl border border-gray-200/50'
        }`}>
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <Lock className={`w-12 h-12 ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
            </div>
            <h1 className={`text-2xl font-bold mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Manukai API Documentation
            </h1>
            <p className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Protected Access Required
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Access Key
              </label>
              <input
                type="password"
                value={authKey}
                onChange={(e) => setAuthKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAuthentication()}
                placeholder="Enter your access key"
                className={`w-full px-4 py-3 rounded-lg border transition-all ${
                  isDark 
                    ? 'bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 focus:border-blue-500 focus:bg-gray-900/70' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
              />
            </div>

            {authError && (
              <div className={`p-3 rounded-lg text-sm ${
                isDark 
                  ? 'bg-red-900/20 text-red-400 border border-red-800/50' 
                  : 'bg-red-50 text-red-600 border border-red-200'
              }`}>
                {authError}
              </div>
            )}

            <button
              onClick={handleAuthentication}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                isDark 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700' 
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
              } shadow-lg hover:shadow-xl`}
            >
              <LogIn className="w-5 h-5" />
              Access Documentation
            </button>
          </div>

          <div className={`mt-6 pt-6 border-t text-center ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <p className={`text-sm ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Need access? Contact{' '}
              <a href="mailto:marc@manuk.ai" className={`hover:underline ${
                isDark ? 'text-blue-400' : 'text-blue-600'
              }`}>
                marc@manuk.ai
              </a>
            </p>
          </div>

          {/* Theme toggle in corner */}
          <button
            onClick={() => setIsDark(!isDark)}
            className={`absolute top-4 right-4 p-2 rounded-lg transition-all ${
              isDark 
                ? 'bg-gray-700/60 text-yellow-400 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </div>
    );
  }

  // Main Documentation Content
  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50'
    }`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        isDark 
          ? 'bg-gray-900/80 backdrop-blur-2xl border-gray-800 shadow-2xl shadow-black/50' 
          : 'bg-white/80 backdrop-blur-2xl border-gray-200 shadow-lg'
      } border-b`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Left Section - Logo */}
            <div className="flex items-center">
              <img 
                key={isDark ? 'dark' : 'light'}
                src={isDark ? "/Manukai_fulltext_white.png" : "/Manukai_fulltext_black.png"}
                alt="Manukai API" 
                className="h-12"
              />
            </div>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-2xl mx-8 relative">
              <div className={`relative transition-all duration-300 ${
                searchFocused ? 'scale-105' : 'scale-100'
              }`}>
                <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors ${
                  searchFocused 
                    ? (isDark ? 'text-blue-400' : 'text-blue-600')
                    : (isDark ? 'text-gray-500' : 'text-gray-400')
                }`} />
                <input
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  className={`w-full pl-12 pr-12 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                    isDark 
                      ? 'bg-gray-800/60 backdrop-blur-xl border-2 border-gray-700/50 text-white placeholder-gray-400 focus:bg-gray-800/80 focus:border-blue-500' 
                      : 'bg-white/60 backdrop-blur-xl border-2 border-gray-300/50 text-gray-900 placeholder-gray-500 focus:bg-white/80 focus:border-blue-500'
                  } focus:ring-4 ${isDark ? 'focus:ring-blue-500/20' : 'focus:ring-blue-500/20'} focus:outline-none`}
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setShowSearchResults(false);
                    }}
                    className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${
                      isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Search Results */}
              {showSearchResults && searchResults.length > 0 && (
                <div className={`absolute top-full mt-2 w-full rounded-xl shadow-2xl overflow-hidden border backdrop-blur-2xl z-50 ${
                  isDark 
                    ? 'bg-gray-800/95 border-gray-700' 
                    : 'bg-white/95 border-gray-200'
                }`}>
                  <div className="max-h-96 overflow-y-auto">
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleSearchResultClick(result.id)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                          isDark 
                            ? 'hover:bg-gray-700/50 border-gray-700' 
                            : 'hover:bg-gray-100 border-gray-200'
                        } border-b last:border-b-0`}
                      >
                        <result.icon className={`w-4 h-4 flex-shrink-0 ${
                          isDark ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {result.title}
                            </span>
                            {result.badge && <ComingSoonBadge />}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {result.category}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Section - Partners, Theme Toggle, and Logout */}
            <div className="flex items-center gap-5">
              {/* Partners Section */}
              <div className="flex flex-col justify-center gap-1">
                <span className={`text-xs font-semibold uppercase tracking-[0.52em] leading-none ${
                  isDark ? 'text-gray-300/95' : 'text-gray-600/95'
                }`}>
                  Partners of
                </span>
                <div className="relative">
                  <img
                    src="/dyami.png"
                    alt="Dyami"
                    className={`h-8 ${
                      isDark 
                        ? 'brightness-110 contrast-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]' 
                        : 'brightness-90 contrast-110 drop-shadow-[0_0_8px_rgba(0,0,0,0.1)]'
                    }`}
                  />
                </div>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className={`p-2.5 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl ${
                  isDark 
                    ? 'bg-red-900/60 backdrop-blur-xl border border-red-800/50 text-red-400 hover:bg-red-800/80' 
                    : 'bg-red-50 backdrop-blur-xl border border-red-200 text-red-600 hover:bg-red-100'
                }`}
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>

              {/* Theme Toggle Button */}
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2.5 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl ${
                  isDark 
                    ? 'bg-gray-800/60 backdrop-blur-xl border border-gray-700/50 text-yellow-400 hover:bg-gray-700/80' 
                    : 'bg-white/60 backdrop-blur-xl border border-gray-300/50 text-gray-700 hover:bg-white/80'
                }`}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <aside className="w-72 flex-shrink-0">
            <nav className={`sticky top-28 rounded-2xl p-6 shadow-2xl transition-colors duration-300 ${
              isDark 
                ? 'bg-gray-800/40 backdrop-blur-2xl border border-gray-700/50' 
                : 'bg-white/60 backdrop-blur-2xl border border-gray-200/50'
            }`}>
              {Object.entries(groupedNav).map(([category, items]) => (
                <div key={category} className="mb-6 last:mb-0">
                  <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {category}
                  </h3>
                  <ul className="space-y-1">
                    {items.map((item) => (
                      <li key={item.id}>
                        <button
                          onClick={() => {
                            setActiveSection(item.id);
                            sectionRefs.current[item.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-all duration-300 flex items-center gap-2 font-medium ${
                            activeSection === item.id
                              ? (isDark 
                                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' 
                                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg')
                              : (isDark 
                                  ? 'text-gray-300 hover:bg-gray-700/50 hover:text-white' 
                                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                          }`}
                        >
                          <item.icon className="w-3.5 h-3.5" />
                          <span className="flex-1">{item.title}</span>
                          {item.badge && <Zap className="w-3 h-3 text-yellow-400" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Section id="introduction" title="Introduction">
              <div className="prose max-w-none">
                <p className={`text-lg mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Welcome to the Manukai API v1.0. This platform provides AI-powered security intelligence across general and aviation domains, 
                  processing thousands of sources daily to identify, score, and analyse emerging threats.
                </p>
                
                <div className={`rounded-2xl p-6 mb-6 border shadow-xl ${
                  isDark 
                    ? 'bg-blue-900/20 backdrop-blur-xl border-blue-800/50' 
                    : 'bg-blue-50/80 backdrop-blur-xl border-blue-200/50'
                }`}>
                  <h3 className={`font-bold mb-3 text-lg ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>
                    Understanding Our Intelligence Products
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className={`font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <AlertTriangle className="w-5 h-5 text-orange-500" />
                        Live Intelligence
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Individual intelligence items analysed and scored for security relevance. Each item receives preliminary triage scores (0-10) 
                        and undergoes deep AI analysis extracting temporal data, threat profiles, actor identification, and risk assessments. 
                        This is raw intelligence at the item level - the foundation of all other products.
                      </p>
                    </div>

                    <div className={`p-4 rounded-xl border ${
                      isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <h4 className={`font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                        <AlertTriangle className="w-5 h-5" />
                        Live Alerts <ComingSoonBadge />
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                        AI-grouped and synthesised intelligence. Multiple related items are clustered into coherent alert events with 
                        consolidated threat assessments, impact analysis, and recommended actions. Think of this as "situational awareness" - 
                        understanding the bigger picture from multiple intelligence signals.
                      </p>
                    </div>

                    <div className={`p-4 rounded-xl border ${
                      isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <h4 className={`font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                        <TrendingUp className="w-5 h-5" />
                        Predictive Alerts <ComingSoonBadge />
                      </h4>
                      <p className={`text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                        Forward-looking threat forecasts based on historical patterns, precursor indicators, and AI-powered trend analysis. 
                        Identifies emerging risks before they materialise into active threats. This is "strategic foresight" - anticipating what's coming.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`rounded-2xl p-6 mb-6 border shadow-xl ${
                  isDark 
                    ? 'bg-gray-800/40 backdrop-blur-xl border-gray-700/50' 
                    : 'bg-white/80 backdrop-blur-xl border-gray-200/50'
                }`}>
                  <h3 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Understanding Preliminary Scores</h3>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Each item undergoes initial AI triage that produces preliminary scores. These scores determine whether deeper analysis is warranted:
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className={`rounded-lg p-4 border ${isDark ? 'bg-gray-900/30 border-gray-700' : 'bg-gray-50 border-gray-300'}`}>
                      <h4 className={`font-semibold mb-2 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>General Scores (ppl_*)</h4>
                      <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li><code>ppl_score</code>: General security relevance (0-10)</li>
                        <li><code>ppl_severity</code>: Threat severity assessment (0-10)</li>
                        <li><code>ppl_directness</code>: Threat proximity</li>
                        <li className="ml-4">• <code>D</code> = Direct threat</li>
                        <li className="ml-4">• <code>I</code> = Indirect threat</li>
                      </ul>
                    </div>
                    
                    <div className={`rounded-lg p-4 border ${isDark ? 'bg-gray-900/30 border-gray-700' : 'bg-gray-50 border-gray-300'}`}>
                      <h4 className={`font-semibold mb-2 ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>Aviation Scores (air_*)</h4>
                      <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li><code>air_score</code>: Aviation security relevance (0-10)</li>
                        <li><code>air_severity</code>: Threat severity assessment (0-10)</li>
                        <li><code>air_directness</code>: Threat proximity</li>
                        <li className="ml-4">• <code>D</code> = Direct threat</li>
                        <li className="ml-4">• <code>I</code> = Indirect threat</li>
                      </ul>
                    </div>
                  </div>
                  
                  <p className={`text-xs mt-4 italic ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    These preliminary scores from the triage phase help prioritise which items receive comprehensive analysis.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className={`rounded-xl p-5 border ${
                    isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white/80 border-gray-200/50'
                  }`}>
                    <h4 className={`font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <Shield className="w-5 h-5 text-blue-500" />
                      General Security
                    </h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Broad threat intelligence across 9 categories: Military Operations, Political Violence, Civil Unrest, 
                      Organised Crime, Espionage, Political/Regulatory, Economic, Operational Infrastructure, and Other.
                    </p>
                  </div>

                  <div className={`rounded-xl p-5 border ${
                    isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white/80 border-gray-200/50'
                  }`}>
                    <h4 className={`font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <Plane className="w-5 h-5 text-indigo-500" />
                      Aviation Security
                    </h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Aviation-specific intelligence across 10 specialised categories covering all phases of aviation operations from 
                      pre-departure through flight operations and cross-cutting threats.
                    </p>
                  </div>
                </div>

                <div className={`rounded-2xl p-6 border shadow-xl ${
                  isDark 
                    ? 'bg-gray-800/40 backdrop-blur-xl border-gray-700/50' 
                    : 'bg-white/80 backdrop-blur-xl border-gray-200/50'
                }`}>
                  <h3 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Base URL</h3>
                  <code className={`text-sm font-mono block p-3 rounded-lg ${
                    isDark ? 'bg-gray-900/50 text-blue-300' : 'bg-gray-50 text-blue-700'
                  }`}>
                    {API_BASE}
                  </code>
                </div>
              </div>
            </Section>

            <Section id="authentication" title="Authentication">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                All API endpoints require authentication via API key passed in the Authorization header.
              </p>
              
              <div className={`rounded-2xl p-8 mb-8 border shadow-xl ${
                isDark 
                  ? 'bg-gray-800/40 backdrop-blur-xl border-gray-700/50' 
                  : 'bg-white/80 backdrop-blur-xl border-gray-200/50'
              }`}>
                <h3 className={`font-bold text-lg mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Authentication Format
                </h3>
                <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Include your API key in the Authorization header:
                </p>
                <code className={`block p-3 rounded-lg font-mono text-sm ${
                  isDark ? 'bg-gray-900/50 text-green-300' : 'bg-gray-50 text-green-700'
                }`}>
                  Authorization: Bearer YOUR_API_KEY
                </code>
              </div>
            </Section>

            <Section id="quickstart" title="Quick Start">
              <div className={`bg-gradient-to-r p-8 rounded-2xl mb-8 shadow-2xl ${
                isDark 
                  ? 'from-blue-600 to-indigo-700 text-white' 
                  : 'from-blue-500 to-indigo-600 text-white'
              }`}>
                <h3 className="font-bold text-2xl mb-4">Get Started in 60 Seconds</h3>
                <p className="mb-6">Fetch the latest security intelligence:</p>
                
                <pre className="bg-black/20 backdrop-blur-xl p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{`curl -X GET "${API_BASE}/intelligence/general" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -G -d "limit=10"`}</code>
                </pre>
                
                <p className="text-sm mt-4 opacity-90">
                  This returns the 10 most recent intelligence items with full threat analysis.
                </p>
              </div>
            </Section>

            <Section id="pagination" title="Pagination Best Practices">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                All list endpoints use cursor-based pagination. Check the <code>has_more</code> boolean to determine if more results exist.
              </p>

              <div className={`rounded-xl p-6 mb-6 border ${
                isDark ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50 border-blue-200'
              }`}>
                <h4 className={`font-bold mb-3 ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>
                  Response Metadata
                </h4>
                
                <pre className={`p-4 rounded-lg text-xs overflow-x-auto ${
                  isDark ? 'bg-gray-900/50 text-gray-300' : 'bg-gray-50 text-gray-800'
                }`}>
                  <code>{`{
  "intelligence": [...],        // Array of items
  "total": 1523,                // Total matching items
  "limit": 100,                 // Requested limit
  "returned": 100,              // Items in this response
  "has_more": true,             // Whether more results exist
  "next_from": 1703894400,      // Cursor for next page (nullable)
  "from": 1703980800,           // Query start timestamp
  "to": 1704067200              // Query end timestamp
}`}</code>
                </pre>
              </div>

              <div className={`rounded-xl p-6 mb-6 border ${
                isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white border-gray-200/50'
              }`}>
                <h4 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Implementation
                </h4>
                
                <pre className={`p-4 rounded-lg text-xs overflow-x-auto ${
                  isDark ? 'bg-gray-900/50 text-gray-300' : 'bg-gray-50 text-gray-800'
                }`}>
                  <code>{`let cursor = null;
let allItems = [];

async function loadMore() {
  const params = new URLSearchParams({ limit: 100 });
  if (cursor) params.append('from_time', cursor);
  
  const response = await fetch(\`\${API_BASE}/intelligence/general?\${params}\`, {
    headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
  });
  const data = await response.json();
  
  allItems = [...allItems, ...data.intelligence];
  
  if (data.has_more) {
    cursor = data.next_from;  // Continue pagination
  } else {
    cursor = null;             // End reached
  }
}`}</code>
                </pre>
              </div>

              <div className={`p-5 rounded-xl border ${
                isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <h4 className={`font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                  <AlertTriangle className="w-5 h-5" />
                  Key Points
                </h4>
                <ul className={`text-sm space-y-1 ml-6 list-disc ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  <li>Always check <code>has_more</code> to determine if pagination should continue</li>
                  <li>Getting fewer items than requested doesn't mean you've reached the end</li>
                  <li>Use <code>next_from</code> cursor when <code>has_more</code> is true</li>
                  <li>Use <code>from_time</code> parameter (not <code>from</code>) to paginate with the cursor</li>
                </ul>
              </div>
            </Section>

            {/* GENERAL SECURITY - LIVE INTELLIGENCE */}
            <Section id="general-intelligence" title="General Security: Live Intelligence">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Get a list of recent general security intelligence. Returns comprehensive summaries with location data and risk scoring.
              </p>

              <Endpoint
                method="GET"
                path="/intelligence/general"
                description="Get general security intelligence list"
                auth={true}
                params={[
                  { name: 'limit', type: 'integer', description: 'Number of results to return', default: '100 (max 500)' },
                  { name: 'from_time', type: 'unix timestamp', description: 'Start time (inclusive)', default: 'now - 48h' },
                  { name: 'to_time', type: 'unix timestamp', description: 'End time (exclusive)', default: 'now' },
                ]}
                example={`curl -X GET "${API_BASE}/intelligence/general?limit=50" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
                response={`{
  "intelligence": [
    {
      "id": "550e8400-e29b-41d4-a716-446655440000",          // Unique UUID for this intelligence item
      "timestamp": 1704067200,                                // Unix timestamp when item was published
      "preliminary": {
        "ppl_score": 7.2,                                     // General security score 0-10 (nullable)
        "ppl_severity": 8.1,                                  // Threat severity 0-10 (nullable)
        "ppl_directness": "D",                                // 'D' = Direct, 'I' = Indirect (nullable)
        "air_score": 3.6,                                     // Aviation security score 0-10 (nullable)
        "air_severity": 4.2,                                  // Threat severity 0-10 (nullable)
        "air_directness": "I"                                 // 'D' = Direct, 'I' = Indirect (nullable)
      },
      "risk_calculation": {
        "max_category_score": 0.95,                           // Highest threat category score 0-1
        "threat_severity_score": 0.82,                        // How severe the threat is 0-1
        "threat_likeliness": 0.88,                            // Probability of threat materialising 0-1
        "final_risk_score_ppl": 8.77,                         // Computed final risk 0-10 for general (nullable)
        "final_risk_score_air": null                          // Computed final risk 0-10 for aviation (nullable)
      },
      "event_tense": "ONGOING",                               // PAST_COMPLETED, ONGOING, IMMINENT, FUTURE_PLANNED
      "summaries": {
        "short": "Bakhmut under assault",                    // One-line summary
        "medium": "Russian forces advance in Donetsk..."     // Brief summary
      },
      "location": {
        "street": null,                                       // Street address (nullable)
        "city": "Bakhmut",                                    // City name (nullable)
        "region": "Donetsk Oblast",                           // State/Oblast/Province (nullable)
        "country": "Ukraine",                                 // Country name (nullable)
        "coordinates": {                                      // Geographic coordinates (nullable as a whole)
          "longitude": 37.9833,
          "latitude": 48.5833
        }
      },
      "source": {
        "id": 123,                                            // Source ID in our database
        "name": "Interfax",                                   // Publisher name
        "country": "Russia",                                  // Source country (nullable)
        "type": "National"                                    // National, Regional, Local, International (nullable)
      },
      "categories": ["Military"],                             // Threat categories (can be empty array)
      "keywords": ["military", "offensive", "Bakhmut"]        // Extracted keywords (can be empty array)
    }
  ],
  "total": 1523,                                              // Total matching results in database
  "limit": 50,                                                // Requested limit
  "returned": 50,                                             // Actual number of results returned
  "has_more": true,                                           // Whether more results exist
  "next_from": 1703980800,                                    // Use this timestamp for next page (nullable)
  "from": 1703980800,                                         // Query start timestamp
  "to": 1704067200                                            // Query end timestamp
}`}
              />
            </Section>

            {/* GENERAL SECURITY - DETAIL VIEW */}
            <Section id="general-intelligence-detail" title="General Security: Detail View">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Get complete intelligence data for a single item including full temporal analysis, threat profiles, and extracted content.
              </p>

              <Endpoint
                method="GET"
                path="/intelligence/general/{id}"
                description="Get detailed intelligence for a single item"
                auth={true}
                example={`curl -X GET "${API_BASE}/intelligence/general/550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
                response={`{
  "id": "550e8400-e29b-41d4-a716-446655440000",
  "timestamp": 1704067200,                                    // Unix timestamp of publication
  "article": {
    "url": "https://interfax.ru/article123",                 // Original article URL
    "language": "ru",                                         // Language code (nullable)
    "original_title": "Российские силы наступают...",        // Original title (nullable)
    "original_content": "Полный текст статьи...",            // Original content (nullable)
    "english_title": "Russian forces advance...",            // English title (nullable)
    "english_content": "Full article text..."                // English content (nullable)
  },
  "source": {
    "id": 123,
    "name": "Interfax",
    "country": "Russia",                                      // nullable
    "type": "National",                                       // National, Regional, Local, International (nullable)
    "url": "https://interfax.ru"                              // nullable
  },
  "preliminary": {
    "ppl_score": 7.2,                                         // nullable
    "ppl_severity": 8.1,                                      // nullable
    "ppl_directness": "D",                                    // 'D' or 'I' (nullable)
    "air_score": 3.6,                                         // nullable
    "air_severity": 4.2,                                      // nullable
    "air_directness": "I"                                     // 'D' or 'I' (nullable)
  },
  "risk_calculation": {
    "max_category_score": 0.95,                               // Highest category threat score
    "threat_severity_score": 0.82,                            // How severe the threat is
    "threat_likeliness": 0.88,                                // Probability it occurs
    "final_risk_score_ppl": 8.77,                             // Computed final score 0-10 (nullable)
    "final_risk_score_air": null,                             // Computed final score 0-10 (nullable)
    "formula_breakdown": {
      "category_component": 0.285,                            // Category contribution
      "severity_component": 0.328,                            // Severity contribution
      "likeliness_component": 0.264,                          // Likeliness contribution
      "coefficients_used": {
        "category": 0.3,                                      // Weight for category
        "severity": 0.4,                                      // Weight for severity
        "likeliness": 0.3                                     // Weight for likeliness
      }
    }
  },
  "temporal": {
    "event_tense": "ONGOING",                                 // PAST_COMPLETED, ONGOING, IMMINENT, FUTURE_PLANNED
    "threat_window": {
      "start": "immediate",                                   // When threat begins (nullable)
      "start_date": null,                                     // Specific date if applicable (nullable)
      "duration": "days"                                      // Expected duration (nullable)
    },
    "probability_timeline": {
      "0_24h": 0.85,                                          // Next 24 hours (nullable)
      "24_72h": 0.70,                                         // 24-72 hours (nullable)
      "3_7d": 0.45,                                           // 3-7 days (nullable)
      "7_30d": 0.20                                           // 7-30 days (nullable)
    }
  },
  "threat_profile": {
    "actor_type": "state_actor",                              // state_actor, non_state_actor, terrorist_group, etc. (nullable)
    "actor_names": ["Russian military", "Wagner Group"],      // Identified actors (can be empty)
    "threat_category_ppl": {                                  // General security categories with scores
      "Military": 0.95,
      "Political_Violence": 0.30
    },
    "threat_category_air": null,                              // Aviation categories (nullable)
    "threat_nature": "kinetic military offensive",            // Type of threat (nullable)
    "methods_and_capabilities": [                             // Tactics/methods used (can be empty)
      "artillery bombardment",
      "mechanised infantry assault"
    ],
    "threat_description": "Large-scale coordinated..."       // Detailed description (nullable)
  },
  "locations": {
    "primary_affected": {
      "street": null,                                         // nullable
      "city": "Bakhmut",                                      // nullable
      "region": "Donetsk Oblast",                             // nullable
      "country": "Ukraine",                                   // nullable
      "coordinates": {                                        // nullable as a whole
        "longitude": 37.9833,
        "latitude": 48.5833
      }
    },
    "secondary_affected": [                                   // Additional affected locations (can be empty)
      {
        "street": null,
        "city": "Soledar",
        "region": "Donetsk Oblast",
        "country": "Ukraine",
        "coordinates": {
          "longitude": 38.0833,
          "latitude": 48.6833
        }
      }
    ],
    "related_location": {
      "country": "Russia",                                    // nullable
      "coordinates": null                                     // nullable
    }
  },
  "content": {
    "keywords": [                                             // Key terms identified (can be empty)
      "military offensive",
      "Bakhmut",
      "Donetsk"
    ],
    "summaries": {
      "short": "Bakhmut under assault",                      // One-liner (nullable)
      "medium": "Russian forces advance in...",               // Brief summary (nullable)
      "long": "Russian military forces have..."               // Detailed summary (nullable)
    },
    "facts": [                                                // Key factual points (can be empty)
      "Russian forces captured two positions",
      "Artillery fire increased in region"
    ],
    "rhetoric": [                                             // Rhetorical elements (can be empty)
      "Forces pushed forward aggressively"
    ]
  },
  "risk_assessment": {
    "threat_severity_score": 0.82,                            // How severe 0-1 (nullable)
    "threat_likeliness": 0.88,                                // How likely 0-1 (nullable)
    "max_category_score": 0.95                                // Highest category score 0-1
  }
}`}
              />
            </Section>

            {/* GENERAL SECURITY - ADVANCED FILTERING */}
            <Section id="general-intelligence-advanced" title="General Security: Advanced Filtering">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Comprehensive filtering capabilities for precise intelligence queries across geographic, temporal, categorical, and source dimensions.
              </p>

              <Endpoint
                method="GET"
                path="/intelligence/general/search"
                description="Advanced search with full filtering options"
                auth={true}
                params={[
                  { name: 'from_time', type: 'unix timestamp', description: 'Start time', default: 'now - 48h' },
                  { name: 'to_time', type: 'unix timestamp', description: 'End time', default: 'now' },
                  { name: 'min_risk_score', type: 'float', description: 'Minimum risk score (0-10)', default: '0' },
                  { name: 'max_risk_score', type: 'float', description: 'Maximum risk score (0-10)', default: '10' },
                  { name: 'affected_country', type: 'string', description: 'Filter by affected country', default: 'all' },
                  { name: 'affected_region', type: 'string', description: 'Filter by affected region/state', default: 'all' },
                  { name: 'affected_city', type: 'string', description: 'Filter by affected city', default: 'all' },
                  { name: 'affected_street', type: 'string', description: 'Filter by affected street', default: 'all' },
                  { name: 'source_country', type: 'string', description: 'Filter by news source country', default: 'all' },
                  { name: 'source_type', type: 'string', description: 'Options: National, Regional, Local, International', default: 'all' },
                  { name: 'source_id', type: 'integer', description: 'Filter by specific source ID', default: 'all' },
                  { name: 'category', type: 'string', description: 'Options: Military, Political_Violence, Civil_Unrest, Organised_Crime, Espionage, Political_Regulatory, Economic, Operational_Infrastructure, Other', default: 'all' },
                  { name: 'event_tense', type: 'string', description: 'Options: PAST_COMPLETED, ONGOING, IMMINENT, FUTURE_PLANNED', default: 'all' },
                  { name: 'actor_type', type: 'string', description: 'Options: state_actor, non_state_actor, terrorist_group, criminal_org, hacktivist, insider, environmental, unknown', default: 'all' },
                  { name: 'keyword', type: 'string', description: 'Search in keywords/content', default: 'none' },
                  { name: 'min_severity', type: 'float', description: 'Minimum threat_severity_score (0-1)', default: '0' },
                  { name: 'min_likeliness', type: 'float', description: 'Minimum threat_likeliness (0-1)', default: '0' },
                  { name: 'limit', type: 'integer', description: 'Results per page', default: '100' },
                ]}
                example={`curl -X GET "${API_BASE}/intelligence/general/search" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -G -d "affected_country=Russia" \\
  -d "source_country=Ukraine" \\
  -d "min_risk_score=7.0" \\
  -d "event_tense=ONGOING" \\
  -d "min_severity=0.7" \\
  -d "category=Military"`}
              />

              <div className={`mt-8 p-5 rounded-xl border ${
                isDark ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50 border-blue-200'
              }`}>
                <h4 className={`font-bold mb-3 ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>
                  General Security Threat Categories
                </h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  {[
                    'Military', 'Political_Violence', 'Civil_Unrest', 'Organised_Crime',
                    'Espionage', 'Political_Regulatory', 'Economic', 'Operational_Infrastructure', 'Other'
                  ].map(cat => (
                    <code key={cat} className={`px-2 py-1 rounded ${
                      isDark ? 'bg-gray-800 text-blue-300' : 'bg-white text-blue-700'
                    }`}>
                      {cat}
                    </code>
                  ))}
                </div>
              </div>
            </Section>

            {/* GENERAL SECURITY - THREAT TAXONOMY */}
            <Section id="general-taxonomy" title="General Security: Threat Taxonomy Reference">
              <div className={`p-6 rounded-xl border ${
                isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <BookOpen className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <h3 className={`font-bold text-lg ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                    Coming Soon
                  </h3>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  Comprehensive breakdown of general security threat categories and subcategories across all nine classification domains.
                </p>
                <p className={`text-xs ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  This reference will include detailed descriptions of: Military Operations, Political Violence, Civil Unrest, 
                  Organised Crime, Espionage, Political/Regulatory, Economic, Operational Infrastructure, and Other threat categories.
                </p>
              </div>
            </Section>

            {/* GENERAL SECURITY - COMING SOON */}
            <Section id="general-live-alerts" title="General Security: Live Alerts">
              <div className={`p-6 rounded-xl border ${
                isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <h3 className={`font-bold text-lg ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                    Coming Soon
                  </h3>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  AI-grouped threat alerts that cluster related intelligence into coherent situational events.
                </p>
                <p className={`text-xs ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  <strong>Expected endpoint:</strong> <code>/intelligence/general/alerts</code>
                </p>
              </div>
            </Section>

            <Section id="general-predictive" title="General Security: Predictive Alerts">
              <div className={`p-6 rounded-xl border ${
                isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <h3 className={`font-bold text-lg ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                    Coming Soon
                  </h3>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  Forward-looking threat forecasts based on pattern analysis and AI modelling.
                </p>
                <p className={`text-xs ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  <strong>Expected endpoint:</strong> <code>/intelligence/general/predictive</code>
                </p>
              </div>
            </Section>

            {/* AVIATION SECURITY - LIVE INTELLIGENCE */}
            <Section id="aviation-intelligence" title="Aviation Security: Live Intelligence">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Get a list of recent aviation security intelligence with comprehensive location and risk data.
              </p>

              <Endpoint
                method="GET"
                path="/intelligence/aviation"
                description="Get aviation security intelligence list"
                auth={true}
                params={[
                  { name: 'limit', type: 'integer', description: 'Number of results to return', default: '100 (max 500)' },
                  { name: 'from_time', type: 'unix timestamp', description: 'Start time (inclusive)', default: 'now - 48h' },
                  { name: 'to_time', type: 'unix timestamp', description: 'End time (exclusive)', default: 'now' },
                ]}
                example={`curl -X GET "${API_BASE}/intelligence/aviation?limit=50" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
                response={`{
  "intelligence": [
    {
      "id": "avi-550e8400-e29b-41d4-a716-446655440000",
      "timestamp": 1704067200,                                // Unix timestamp of publication
      "preliminary": {
        "ppl_score": 3.2,                                     // nullable
        "ppl_severity": 3.8,                                  // nullable
        "ppl_directness": "I",                                // 'D' or 'I' (nullable)
        "air_score": 8.5,                                     // nullable
        "air_severity": 8.9,                                  // nullable
        "air_directness": "D"                                 // 'D' or 'I' (nullable)
      },
      "risk_calculation": {
        "max_category_score": 0.95,
        "threat_severity_score": 0.88,
        "threat_likeliness": 0.96,
        "final_risk_score_ppl": null,                         // nullable
        "final_risk_score_air": 8.92                          // nullable
      },
      "event_tense": "ONGOING",                               // PAST_COMPLETED, ONGOING, IMMINENT, FUTURE_PLANNED
      "summaries": {
        "short": "GPS jamming detected",
        "medium": "Military GPS jamming affecting routes..."
      },
      "location": {
        "street": null,                                       // nullable
        "city": "Kaliningrad",                                // nullable
        "region": "Kaliningrad Oblast",                       // nullable
        "country": "Russia",                                  // nullable
        "coordinates": {                                      // nullable as a whole
          "longitude": 20.5083,
          "latitude": 54.7065
        }
      },
      "source": {
        "id": 456,
        "name": "FlightRadar24",
        "country": "Sweden",                                  // nullable
        "type": "International"                               // nullable
      },
      "categories": ["Overflight", "Cyber_Information"],      // can be empty
      "keywords": ["GPS jamming", "navigation"]               // can be empty
    }
  ],
  "total": 423,
  "limit": 50,
  "returned": 50,
  "has_more": true,
  "next_from": 1703980800,                                    // nullable
  "from": 1703980800,
  "to": 1704067200
}`}
              />
            </Section>

            {/* AVIATION SECURITY - DETAIL VIEW */}
            <Section id="aviation-intelligence-detail" title="Aviation Security: Detail View">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Get complete aviation intelligence data for a single item.
              </p>

              <Endpoint
                method="GET"
                path="/intelligence/aviation/{id}"
                description="Get detailed aviation intelligence"
                auth={true}
                example={`curl -X GET "${API_BASE}/intelligence/aviation/avi-550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
                response={`{
  "id": "avi-550e8400-e29b-41d4-a716-446655440000",
  "timestamp": 1704067200,
  "article": {
    "url": "https://flightradar24.com/blog/article123",
    "language": "en",                                         // nullable
    "original_title": "GPS Jamming Detected Near...",        // nullable
    "original_content": "Full article text...",              // nullable
    "english_title": "GPS Jamming Detected Near...",        // nullable
    "english_content": "Full article text..."                // nullable
  },
  "source": {
    "id": 456,
    "name": "FlightRadar24",
    "country": "Sweden",                                      // nullable
    "type": "International",                                  // nullable
    "url": "https://flightradar24.com"                        // nullable
  },
  "preliminary": {
    "ppl_score": 3.2,                                         // nullable
    "ppl_severity": 3.8,                                      // nullable
    "ppl_directness": "I",                                    // nullable
    "air_score": 8.5,                                         // nullable
    "air_severity": 8.9,                                      // nullable
    "air_directness": "D"                                     // nullable
  },
  "risk_calculation": {
    "max_category_score": 0.95,
    "threat_severity_score": 0.88,
    "threat_likeliness": 0.96,
    "final_risk_score_ppl": null,                             // nullable
    "final_risk_score_air": 8.92,                             // nullable
    "formula_breakdown": {
      "category_component": 0.285,
      "severity_component": 0.352,
      "likeliness_component": 0.288,
      "coefficients_used": {
        "category": 0.3,
        "severity": 0.4,
        "likeliness": 0.3
      }
    }
  },
  "temporal": {
    "event_tense": "ONGOING",
    "threat_window": {
      "start": "immediate",                                   // nullable
      "start_date": null,                                     // nullable
      "duration": "persistent"                                // nullable
    },
    "probability_timeline": {
      "0_24h": 0.95,                                          // nullable
      "24_72h": 0.92,                                         // nullable
      "3_7d": 0.88,                                           // nullable
      "7_30d": 0.75                                           // nullable
    }
  },
  "threat_profile": {
    "actor_type": "state_actor",                              // nullable
    "actor_names": ["Russian military"],                      // can be empty
    "threat_category_ppl": null,                              // nullable
    "threat_category_air": {                                  // Aviation categories
      "Overflight": 0.95,
      "Cyber_Information": 0.85,
      "Area_Regional": 0.70
    },
    "threat_nature": "electronic warfare GPS jamming",        // nullable
    "methods_and_capabilities": [                             // can be empty
      "GPS signal jamming",
      "GNSS interference"
    ],
    "threat_description": "Persistent GPS jamming..."        // nullable
  },
  "locations": {
    "primary_affected": {
      "street": null,
      "city": "Kaliningrad",
      "region": "Kaliningrad Oblast",
      "country": "Russia",
      "coordinates": {
        "longitude": 20.5083,
        "latitude": 54.7065
      }
    },
    "secondary_affected": [                                   // can be empty
      {
        "street": null,
        "city": "Gdansk",
        "region": "Pomeranian Voivodeship",
        "country": "Poland",
        "coordinates": {
          "longitude": 18.6466,
          "latitude": 54.3520
        }
      }
    ],
    "related_location": {
      "country": "Russia",
      "coordinates": null
    }
  },
  "content": {
    "keywords": [
      "GPS jamming",
      "navigation interference"
    ],
    "summaries": {
      "short": "GPS jamming detected",                       // nullable
      "medium": "Military GPS jamming affecting routes...",   // nullable
      "long": "Persistent GPS signal jamming detected..."     // nullable
    },
    "facts": [                                                // can be empty
      "GPS jamming detected near Kaliningrad",
      "Multiple flights reported anomalies"
    ],
    "rhetoric": [                                             // can be empty
      "Military-grade equipment suspected"
    ]
  },
  "risk_assessment": {
    "threat_severity_score": 0.88,                            // nullable
    "threat_likeliness": 0.96,                                // nullable
    "max_category_score": 0.95
  }
}`}
              />
            </Section>

            {/* AVIATION SECURITY - ADVANCED FILTERING */}
            <Section id="aviation-intelligence-advanced" title="Aviation Security: Advanced Filtering">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Comprehensive filtering for aviation intelligence with category-specific and location-based queries.
              </p>

              <Endpoint
                method="GET"
                path="/intelligence/aviation/search"
                description="Advanced aviation intelligence search"
                auth={true}
                params={[
                  { name: 'from_time', type: 'unix timestamp', description: 'Start time', default: 'now - 48h' },
                  { name: 'to_time', type: 'unix timestamp', description: 'End time', default: 'now' },
                  { name: 'min_risk_score', type: 'float', description: 'Minimum risk score (0-10)', default: '0' },
                  { name: 'max_risk_score', type: 'float', description: 'Maximum risk score (0-10)', default: '10' },
                  { name: 'affected_country', type: 'string', description: 'Filter by affected country', default: 'all' },
                  { name: 'affected_region', type: 'string', description: 'Filter by affected region/state', default: 'all' },
                  { name: 'affected_city', type: 'string', description: 'Filter by affected city/airport', default: 'all' },
                  { name: 'affected_street', type: 'string', description: 'Filter by specific location', default: 'all' },
                  { name: 'source_country', type: 'string', description: 'Filter by news source country', default: 'all' },
                  { name: 'source_type', type: 'string', description: 'Options: National, Regional, Local, International', default: 'all' },
                  { name: 'source_id', type: 'integer', description: 'Filter by specific source ID', default: 'all' },
                  { name: 'category', type: 'string', description: 'Options: City_Hotel, Landside, Airport, Airside_Cargo, Business_Aviation_FBO, Overflight, In_Flight, Area_Regional, Cyber_Information, Emerging_Threats', default: 'all' },
                  { name: 'event_tense', type: 'string', description: 'Options: PAST_COMPLETED, ONGOING, IMMINENT, FUTURE_PLANNED', default: 'all' },
                  { name: 'actor_type', type: 'string', description: 'Options: state_actor, non_state_actor, terrorist_group, criminal_org, hacktivist, insider, environmental, unknown', default: 'all' },
                  { name: 'keyword', type: 'string', description: 'Search keywords', default: 'none' },
                  { name: 'min_severity', type: 'float', description: 'Minimum threat_severity_score (0-1)', default: '0' },
                  { name: 'min_likeliness', type: 'float', description: 'Minimum threat_likeliness (0-1)', default: '0' },
                  { name: 'limit', type: 'integer', description: 'Results per page', default: '100' },
                ]}
                example={`curl -X GET "${API_BASE}/intelligence/aviation/search" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -G -d "category=Overflight" \\
  -d "affected_country=Poland" \\
  -d "min_risk_score=7.0"`}
              />

              <div className={`mt-8 p-5 rounded-xl border ${
                isDark ? 'bg-indigo-900/20 border-indigo-800/50' : 'bg-indigo-50 border-indigo-200'
              }`}>
                <h4 className={`font-bold mb-3 ${isDark ? 'text-indigo-300' : 'text-indigo-900'}`}>
                  Aviation Threat Categories
                </h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  {[
                    'City_Hotel', 'Landside', 'Airport', 'Airside_Cargo',
                    'Business_Aviation_FBO', 'Overflight', 'In_Flight', 
                    'Area_Regional', 'Cyber_Information', 'Emerging_Threats'
                  ].map(cat => (
                    <code key={cat} className={`px-2 py-1 rounded ${
                      isDark ? 'bg-gray-800 text-indigo-300' : 'bg-white text-indigo-700'
                    }`}>
                      {cat}
                    </code>
                  ))}
                </div>
                <p className={`text-xs mt-3 italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  These categories cover all aviation security phases from pre-departure through flight operations.
                </p>
              </div>
            </Section>

            {/* AVIATION THREAT TAXONOMY */}
            <Section id="aviation-taxonomy" title="Aviation Threat Taxonomy Reference">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Complete breakdown of aviation threat categories and their subcategories across all operational phases.
              </p>

              <div className="space-y-6">
                {/* Phase 1: Pre-Departure */}
                <div className={`rounded-xl border ${isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white border-gray-200'}`}>
                  <div className={`p-5 border-b ${isDark ? 'bg-indigo-900/20 border-gray-700/50' : 'bg-indigo-50 border-gray-200'}`}>
                    <h3 className={`font-bold text-lg ${isDark ? 'text-indigo-300' : 'text-indigo-900'}`}>
                      Phase 1: Pre-Departure
                    </h3>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">City_Hotel</code>
                        City/Hotel Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Attacks on crew hotels and layover facilities</li>
                        <li>• Armed attacks on crew transportation</li>
                        <li>• Kidnapping of flight crew and aviation executives</li>
                        <li>• Targeting of airline offices and crew facilities</li>
                        <li>• Surveillance operations targeting aviation personnel</li>
                        <li>• Bomb threats at crew hotels and airline facilities</li>
                        <li>• Civil unrest or strikes disrupting crew travel to/from airports</li>
                        <li>• Harassment or assault of identifiable aviation personnel</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Phase 2: Airport Arrival & Processing */}
                <div className={`rounded-xl border ${isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white border-gray-200'}`}>
                  <div className={`p-5 border-b ${isDark ? 'bg-indigo-900/20 border-gray-700/50' : 'bg-indigo-50 border-gray-200'}`}>
                    <h3 className={`font-bold text-lg ${isDark ? 'text-indigo-300' : 'text-indigo-900'}`}>
                      Phase 2: Airport Arrival & Processing
                    </h3>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Landside</code>
                        Landside Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Vehicle ramming and active shooter attacks</li>
                        <li>• Knife and edged weapon attacks</li>
                        <li>• Vehicle-borne IEDs (VBIEDs) and person-borne IEDs (suicide bombings)</li>
                        <li>• Hostage-taking incidents</li>
                        <li>• Attacks on passengers in public terminal areas and parking facilities</li>
                        <li>• Attacks on airport ground transportation facilities (train/metro stations)</li>
                        <li>• Organized criminal activity targeting passengers or staff</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Airport</code>
                        Airport Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Unauthorized drone/UAS disrupting operations (including weaponized)</li>
                        <li>• Person-borne explosives (on body or in cabin baggage)</li>
                        <li>• Explosives in checked/hold baggage</li>
                        <li>• Perimeter and restricted area breaches</li>
                        <li>• Cyber-attacks on airport systems</li>
                        <li>• CBRN (chemical, biological, radiological, nuclear) threats</li>
                        <li>• Attacks on critical infrastructure and ATC facilities</li>
                        <li>• Bomb hoaxes causing evacuations and disruptions</li>
                        <li>• Protests and activist disruptions causing operational delays</li>
                        <li>• Theft of security credentials, uniforms, or access equipment</li>
                        <li>• Kidnapping attempts targeting aviation personnel or passengers</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Phase 3: Departure & Aircraft Preparation */}
                <div className={`rounded-xl border ${isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white border-gray-200'}`}>
                  <div className={`p-5 border-b ${isDark ? 'bg-indigo-900/20 border-gray-700/50' : 'bg-indigo-50 border-gray-200'}`}>
                    <h3 className={`font-bold text-lg ${isDark ? 'text-indigo-300' : 'text-indigo-900'}`}>
                      Phase 3: Departure & Aircraft Preparation
                    </h3>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Airside_Cargo</code>
                        Airside/Cargo Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Insider threats and credential theft (airport/airline staff)</li>
                        <li>• Radicalization or coercion of ground crew personnel</li>
                        <li>• Improvised incendiary devices (IIDs) in cargo and mail</li>
                        <li>• Improvised explosive devices (IEDs) in cargo and mail</li>
                        <li>• Explosives in catering, in-flight supplies, and cleaning supplies</li>
                        <li>• Explosives in airport supplies</li>
                        <li>• Aircraft sabotage and tampering on ground (fuel, components, systems)</li>
                        <li>• Sabotage of safety-critical equipment and infrastructure</li>
                        <li>• Sabotage of cargo or mail handling operations</li>
                        <li>• Counterfeit/sabotaged aircraft parts in supply chain</li>
                        <li>• Vehicle-borne airside attacks</li>
                        <li>• Unauthorized airside access and vehicles</li>
                        <li>• Unauthorized access to parked aircraft</li>
                        <li>• Attacks on parked aircraft on airport aprons</li>
                        <li>• Attacks or threats during aircraft refueling operations</li>
                        <li>• Deficiencies in aircraft security searches and protection measures</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Business_Aviation_FBO</code>
                        Business Aviation/FBO Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Attacks, threats, or security breaches at FBO facilities and private terminals</li>
                        <li>• VIP passenger targeting, kidnapping threats, or surveillance</li>
                        <li>• Security breaches or screening failures at business aviation facilities</li>
                        <li>• Theft, sabotage, or tampering with business aircraft</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Phase 4: Flight Operations */}
                <div className={`rounded-xl border ${isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white border-gray-200'}`}>
                  <div className={`p-5 border-b ${isDark ? 'bg-indigo-900/20 border-gray-700/50' : 'bg-indigo-50 border-gray-200'}`}>
                    <h3 className={`font-bold text-lg ${isDark ? 'text-indigo-300' : 'text-indigo-900'}`}>
                      Phase 4: Flight Operations
                    </h3>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Overflight</code>
                        Overflight Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• GPS jamming and spoofing (GNSS interference)</li>
                        <li>• Laser attacks on cockpits</li>
                        <li>• Missile/MANPADS threats (conflict and non-conflict zones)</li>
                        <li>• Military activity creating flight hazards</li>
                        <li>• Radio communications jamming</li>
                        <li>• Rocket and mortar attacks</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">In_Flight</code>
                        In-Flight Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Person-borne IEDs (especially in portable electronic devices)</li>
                        <li>• Unruly/disruptive passengers requiring diversion</li>
                        <li>• Conventional hijacking attempts with terrorist intent</li>
                        <li>• Aircraft used as weapon</li>
                        <li>• Pilot incapacitation or intentional aircraft manipulation</li>
                        <li>• Chemical agent deployment in cabin</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Phase 5: Cross-Cutting Threats */}
                <div className={`rounded-xl border ${isDark ? 'bg-gray-800/40 border-gray-700/50' : 'bg-white border-gray-200'}`}>
                  <div className={`p-5 border-b ${isDark ? 'bg-indigo-900/20 border-gray-700/50' : 'bg-indigo-50 border-gray-200'}`}>
                    <h3 className={`font-bold text-lg ${isDark ? 'text-indigo-300' : 'text-indigo-900'}`}>
                      Phase 5: Cross-Cutting Threats
                    </h3>
                  </div>
                  
                  <div className="p-5 space-y-4">
                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Area_Regional</code>
                        Area/Regional Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Armed conflict impacting flight routes and airport operations</li>
                        <li>• Airspace closures due to diplomatic tensions</li>
                        <li>• Political instability and breakdown of law and order</li>
                        <li>• Weapons proliferation (MANPADS, missiles) in conflict zones</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Cyber_Information</code>
                        Cyber/Information Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Attacks on ATM, aircraft, and airport systems</li>
                        <li>• Attacks on aircraft communication systems (ACARS, CPDLC, SATCOM)</li>
                        <li>• Attacks on air traffic management and control systems</li>
                        <li>• Exploitation of electronic flight bags (EFB) and in-flight entertainment (IFE)</li>
                        <li>• Hacking or vulnerabilities in airport security screening equipment</li>
                        <li>• Manipulation of flight operations or navigation software</li>
                        <li>• Attacks on weather information systems</li>
                        <li>• Ransomware and denial-of-service attacks disrupting operations</li>
                        <li>• Data breaches exposing passenger/crew information</li>
                        <li>• Nation-state cyber espionage targeting aviation systems</li>
                        <li>• State-sponsored surveillance or digital restrictions creating vulnerabilities</li>
                        <li>• Social engineering compromising personnel credentials</li>
                        <li>• Insider cyber threats and privileged access abuse</li>
                        <li>• Compromised IT/OT equipment in aviation supply chains</li>
                        <li>• Manipulation of navigation systems (GPS/GNSS, ADS-B)</li>
                        <li>• Exploitation of connected aircraft vulnerabilities</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <code className="px-2 py-0.5 rounded text-xs bg-indigo-600 text-white">Emerging_Threats</code>
                        Emerging Threats
                      </h4>
                      <ul className={`text-sm space-y-1 ml-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <li>• Novel attack methodologies demonstrating credible capability and intent</li>
                        <li>• Exploitation of new aviation technologies</li>
                        <li>• Unforeseen threat vectors not covered by existing categories</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`mt-6 p-4 rounded-lg border ${
                isDark ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50 border-blue-200'
              }`}>
                <p className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                  <strong>Note:</strong> Categories in the API responses map to the top-level categories shown in <code>code blocks</code> above. 
                  This taxonomy provides comprehensive coverage of aviation security threats across all operational phases.
                </p>
              </div>
            </Section>

            {/* AVIATION - COMING SOON */}
            <Section id="aviation-live-alerts" title="Aviation Security: Live Alerts">
              <div className={`p-6 rounded-xl border ${
                isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <h3 className={`font-bold text-lg ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                    Coming Soon
                  </h3>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  Aviation-specific grouped alerts for route planning and operational safety.
                </p>
                <p className={`text-xs ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  <strong>Expected endpoint:</strong> <code>/intelligence/aviation/alerts</code>
                </p>
              </div>
            </Section>

            <Section id="aviation-predictive" title="Aviation Security: Predictive Alerts">
              <div className={`p-6 rounded-xl border ${
                isDark ? 'bg-yellow-900/20 border-yellow-800/50' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <h3 className={`font-bold text-lg ${isDark ? 'text-yellow-300' : 'text-yellow-900'}`}>
                    Coming Soon
                  </h3>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  Predictive aviation risk modelling for route planning.
                </p>
                <p className={`text-xs ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  <strong>Expected endpoint:</strong> <code>/intelligence/aviation/predictive</code>
                </p>
              </div>
            </Section>

            {/* REFERENCE */}
            <Section id="sources" title="News Sources">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Query our database of sources.
              </p>

              <Endpoint
                method="GET"
                path="/sources"
                description="Get sources with filtering"
                auth={true}
                params={[
                  { name: 'country', type: 'string', description: 'Filter by country', default: 'all' },
                  { name: 'type', type: 'string', description: 'Options: National, Regional, Local, International', default: 'all' },
                  { name: 'search', type: 'string', description: 'Search source names', default: 'none' },
                  { name: 'limit', type: 'integer', description: 'Results per page', default: '100' },
                ]}
                example={`curl -X GET "${API_BASE}/sources?country=Russia&limit=50" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
                response={`{
  "sources": [
    {
      "id": 123,
      "name": "Interfax",
      "country": "Russia",                                    // nullable
      "continent": "Europe",                                  // nullable
      "type": "National",                                     // National, Regional, Local, International (nullable)
      "url": "https://interfax.ru"                            // nullable
    }
  ],
  "total": 245,
  "limit": 50,
  "returned": 50,
  "has_more": true
}`}
              />
            </Section>

            <Section id="health" title="Health & Status">
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Monitor API health and system status.
              </p>

              <Endpoint
                method="GET"
                path="/health"
                description="System health check"
                auth={false}
                example={`curl -X GET "${API_BASE}/health"`}
                response={`{
  "status": "operational",                                    // operational, degraded, down
  "version": "2.0.0",                                         // API version string
  "database": {
    "total_articles": 2500000,                                // Total intelligence items in database
    "intelligence_last_24h": 15234,                           // Items processed in last 24h
    "partitions_active": 0                                    // Active database partitions (legacy field, always 0)
  },
  "workers": {
    "active": 0,                                              // Active worker nodes (reserved for future use)
    "processing_triage": 23,                                  // Items in triage phase
    "processing_translation": 15,                             // Items being translated
    "processing_analysis": 9                                  // Items in advanced analysis
  },
  "uptime_seconds": 864532                                    // System uptime in seconds
}`}
              />
            </Section>

            <div className={`mt-16 pt-8 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className={`text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <p>Manukai API v1.0 • Security Intelligence Platform</p>
                <p className="mt-2">Questions? Contact: <a href="mailto:marc@manuk.ai" className={`hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>marc@manuk.ai</a></p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ManukapiDocs;